import React, { useMemo } from 'react';

import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import map from 'lodash/fp/map';
import pipe from 'lodash/fp/pipe';
import reject from 'lodash/fp/reject';

import { RenderingType } from '@lumapps/communities/types';
import { CommunityNavigation } from '@lumapps/community-navigation';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { ItemVariant, ViewMode } from '@lumapps/lumx-navigation/constants';
import { useSelector } from '@lumapps/redux/react';
import { useParams } from '@lumapps/router';
import { isSpaceEditableByUser } from '@lumapps/spaces/ducks/selectors';
import { type SpaceRouteParameters, type SpaceServices, type SpaceViews } from '@lumapps/spaces/types';
import evolve from '@lumapps/utils/function/evolve';

import { SPACE_NAV_V1_STATIC_ITEMS, SPACE_NAV_V2_EXPLORE_ITEMS, SPACE_NAV_V2_STATIC_ITEMS } from '../../constants';
import { isSpaceNavigationV2Enabled } from '../../ducks/selectors';
import { servicesToItems } from '../../utils';

export interface SpaceNavigationProps {
    enabledServices: SpaceServices[];
}

export const SpaceNavigation: React.FC<SpaceNavigationProps> = ({ enabledServices }) => {
    const { id, slug, view } = useParams<SpaceRouteParameters>();
    const isManager = useSelector(isSpaceEditableByUser);
    const instanceId = useSelector(instanceIdSelector);
    const isSpaceNavigationV2 = useSelector(isSpaceNavigationV2Enabled);

    const exploreItems = useMemo(() => {
        if (isSpaceNavigationV2) {
            return pipe(
                map(evolve({ services: servicesToItems(id, slug, undefined, enabledServices) })),
                reject(pipe(get('services'), isEmpty)),
            )(SPACE_NAV_V2_EXPLORE_ITEMS);
        }

        return undefined;
    }, [enabledServices, id, isSpaceNavigationV2, slug]);

    const staticItems = useMemo(() => {
        const [staticEntries, variant] = isSpaceNavigationV2
            ? [SPACE_NAV_V2_STATIC_ITEMS, ItemVariant.iconOnly]
            : [SPACE_NAV_V1_STATIC_ITEMS, ItemVariant.default];

        return servicesToItems(id, slug, variant, enabledServices, staticEntries);
    }, [enabledServices, id, isSpaceNavigationV2, slug]);

    const communityNavigationQuery = useMemo(
        () => ({
            siteId: instanceId,
            communityId: id,
        }),
        [id, instanceId],
    );

    return (
        <CommunityNavigation
            currentView={view as SpaceViews}
            staticItems={staticItems}
            exploreItems={exploreItems}
            theme="light"
            viewMode={ViewMode.horizontal}
            scope={RenderingType.space}
            isManager={isManager}
            communityNavigationQuery={communityNavigationQuery}
        />
    );
};
